import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useWidgetBackgroundVariant } from '@onramp/hooks/useWidgetBackgroundVariant';
import { logNavCloseMenu, logNavOpenMenu, logNavViewedMenu } from '@onramp/utils/eventing';
import exitWidget from '@onramp/utils/exitWidget';
import { useIsGuestCheckoutPath } from '@onramp/utils/guestCheckoutUtils';
import { isMobileExperience } from '@onramp/utils/postMessage';
import { useReffedFunction } from '@onramp/utils/useReffedFunction';
import { css, cx } from 'linaria';
import { styled } from 'linaria/react';
import { useRouter } from 'next/router';
import { IconButton } from '@cbhq/cds-web/buttons';
import { usePalette } from '@cbhq/cds-web/hooks/usePalette';
import { Icon, LogoWordmark } from '@cbhq/cds-web/icons';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { Link as CDSLink, TextHeadline, TextLegal } from '@cbhq/cds-web/typography';
import { DebugMenu } from './DebugMenu';
import { UserMenu } from './UserMenu';
const CONTAINER_WIDTH = 440;
const CONTAINER_HEIGHT = 700;
const messages = defineMessages({
  securedBy: {
    id: 'Modal.securedBy',
    defaultMessage: 'Secured by',
    description: 'A text label preceded by a lock icon and followed by the Coinbase logo that is shown in every CBPay screen at the footer'
  },
  menuText: {
    id: 'Modal.menuText',
    defaultMessage: 'Menu',
    description: 'Menu text label'
  },
  aboutText: {
    id: 'Modal.aboutText',
    defaultMessage: 'About Coinbase Onramp',
    description: 'About Coinbase Onramp text label'
  }
});
export const Modal: React.FC = ({
  children
}) => {
  const {
    background
  } = usePalette();
  const variant = useWidgetBackgroundVariant();
  return <OuterWrapper>
      <StyledModalContainer>
        <VStack width="100%" height="100%" overflow="hidden" bordered borderRadius="roundedLarge" dangerouslySetBackground={background} className={cx(getVstackStyle, variant === 'overlay' && shadowStyle)}>
          <Box flexGrow={1} overflow="auto" width="100%">
            <VStack width="100%" height="100%" overflow="hidden">
              {children}
            </VStack>
          </Box>
        </VStack>
      </StyledModalContainer>
    </OuterWrapper>;
};
export const ModalHeader: React.FC<{
  title: string;
  onBack?: () => void;
  showUserMenu?: MenuTab;
  /** Show close button in place of menu button. Takes precedence over showUserMenu. */
  showCloseButton?: boolean;
  onShowMenu?: () => void;
  onMenuBack?: () => void;
  bordered?: boolean;
}> = ({
  title,
  onBack,
  showUserMenu,
  onShowMenu,
  onMenuBack,
  bordered,
  showCloseButton
}) => {
  const rightIcon = useMemo(() => {
    if (showCloseButton) {
      return <IconButton name="close" onPress={exitWidget} variant="secondary" transparent aria-hidden="true" testID="close-button" />;
    }
    switch (showUserMenu) {
      case 'account':
        return <IconButton name="close" onPress={onShowMenu} variant="secondary" transparent aria-hidden="true" testID="close-button" />;
      case 'none':
        return <IconButton name="hamburger" onPress={onShowMenu} variant="secondary" transparent aria-hidden="true" testID="hamburger-button" />;
      default:
        return null;
    }
  }, [onShowMenu, showCloseButton, showUserMenu]);
  const shouldHaveBorder = bordered ?? showUserMenu === 'none';
  return <HStack justifyContent="center" alignItems="center" borderedBottom={shouldHaveBorder} width="100%" minHeight={56} spacing={2} position="relative">
      {onBack && (showUserMenu === 'none' || !showUserMenu) ? <PreviousPageLink goBack={onBack} /> : null}
      {showUserMenu === 'about' ? <Box position="absolute" left={10}>
          <IconButton name="backArrow" onPress={onMenuBack} variant="secondary" transparent testID="back-button" />
        </Box> : null}
      <TextHeadline as="h1" testID="modal-title">
        {title}
      </TextHeadline>
      <Box position="absolute" right={10}>
        {rightIcon}
      </Box>
    </HStack>;
};
type PreviousPageLinkProps = {
  goBack: () => void;
};
const PreviousPageLink = ({
  goBack: onBack
}: PreviousPageLinkProps) => {
  const handleOnPress = useCallback(() => onBack(), [onBack]);
  return <Box position="absolute" left={10}>
      <IconButton testID="previous-page-button" name="backArrow" color="foreground" onPress={handleOnPress} transparent />
    </Box>;
};
export const ModalContent: React.FC = ({
  children
}) => {
  return <VStack flexGrow={1} overflow="auto" width="100%" position="relative">
      {children}
    </VStack>;
};
export const ModalDisclaimerWith10PressCallback: React.FC<{
  on10Presses: () => void;
}> = ({
  on10Presses
}) => {
  const [numTimesClicked, setNumTimesClicked] = useState(0);
  const handlePoweredByClick = useCallback(() => {
    setNumTimesClicked(v => v + 1);
  }, []);
  useEffect(() => {
    if (numTimesClicked >= 10) {
      setNumTimesClicked(0);
      on10Presses();
    }
  }, [numTimesClicked, on10Presses]);
  const {
    formatMessage
  } = useIntl();
  const isMobile = isMobileExperience();
  return <HStack justifyContent="center" alignContent="center" alignItems="baseline" borderedTop spacing={1} height={32} maxHeight={32}>
      <Icon color="foregroundMuted" size="xs" name="lock" spacingEnd={1} />
      <TextLegal as="p" color="foregroundMuted" onClick={handlePoweredByClick} className={disclaimerStyle}>
        {formatMessage(messages.securedBy)}
      </TextLegal>
      <CDSLink variant="legal" href={isMobile ? undefined : 'https://coinbase.com'} openInNewWindow rel="noopener">
        <Box width={80} height="1em">
          <LogoWordmark />
        </Box>
      </CDSLink>
    </HStack>;
};
export type ModalLayoutProps = {
  title: string;
  onBack?: () => void;
  children: ReactNode;
  borderedHeader?: boolean;
  hideHamburgerMenu?: boolean;
  /** Shows close button in place of menu button. Takes precedence over hideHamburgerMenu. */
  showCloseButton?: boolean;
};
export type MenuTab = 'none' | 'account' | 'about';
export const ModalLayout: React.FC<ModalLayoutProps> = ({
  title,
  onBack,
  children,
  borderedHeader,
  hideHamburgerMenu = false,
  showCloseButton
}) => {
  const [showDebugMenu, setShowDebugMenu] = useState(false);
  const [menuState, setMenuState] = useState<MenuTab>('none');
  const isGuestCheckoutPath = useIsGuestCheckoutPath();
  const router = useRouter();
  const {
    formatMessage
  } = useIntl();
  const handle10Presses = useReffedFunction(() => {
    setShowDebugMenu(true);
  });
  const handleCloseDebugMenu = useReffedFunction(() => {
    setShowDebugMenu(false);
  });
  const pageName = useMemo(() => {
    switch (menuState) {
      case 'account':
        return formatMessage(messages.menuText);
      case 'about':
        return formatMessage(messages.aboutText);
      default:
        return title;
    }
  }, [formatMessage, menuState, title]);
  const handleShowMenu = useCallback(() => {
    if (menuState === 'none') {
      logNavOpenMenu();
      logNavViewedMenu(title);
      setMenuState('account');
    } else {
      logNavCloseMenu();
      setMenuState('none');
    }
  }, [menuState, title]);
  const handleShowAboutMenu = useCallback(() => setMenuState('about'), []);
  const handleMenuBack = useCallback(() => setMenuState('account'), []);
  const content = useMemo(() => {
    if (showDebugMenu) {
      return <DebugMenu onClose={handleCloseDebugMenu} />;
    }
    if (menuState === 'none') {
      return children;
    }
    return <UserMenu menuState={menuState} onShowMenu={handleShowAboutMenu} />;
  }, [showDebugMenu, menuState, handleShowAboutMenu, handleCloseDebugMenu, children]);
  const isGuestCheckoutOrLanding = isGuestCheckoutPath || router.pathname === '/landing';
  const showUserMenu = !hideHamburgerMenu && !isGuestCheckoutOrLanding;
  return <Modal>
      <ModalHeader title={pageName} onBack={onBack} showUserMenu={showUserMenu ? menuState : undefined} onShowMenu={handleShowMenu} onMenuBack={handleMenuBack} bordered={borderedHeader} showCloseButton={showCloseButton} />
      <ModalContent>{content}</ModalContent>
      <ModalDisclaimerWith10PressCallback on10Presses={handle10Presses} />
    </Modal>;
};
const OuterWrapper = /*#__PURE__*/styled("div")({
  name: "OuterWrapper",
  class: "o6r8vkj"
});
const StyledModalContainer = /*#__PURE__*/styled("div")({
  name: "StyledModalContainer",
  class: "sev75x2"
});

// When horizontally shrunk below CONTAINER_WIDTH, remove border styles from VStack. Used to override VStack bordered propery
const getVstackStyle = "gd3huat";
const shadowStyle = "s1dfa66c";
const disclaimerStyle = "d19zy7f9";

require("./index.linaria.module.css!=!../../../node_modules/linaria/lib/outputCssLoader.js?cacheProvider=!./index.tsx");